import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import HomePage from "@/pages/HomePage";
import AdminPage from "@/pages/AdminPage";
import AuthCallback from "@/components/AuthCallback";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { err: false };
    }
    static getDerivedStateFromError() {
        return { err: true };
    }
    render() {
        if (this.state.err) {
            return (
                <div className="grid min-h-screen place-items-center bg-obsidian text-white">
                    Something went wrong — please refresh the page.
                </div>
            );
        }
        return this.props.children;
    }
}

const SmoothScroll = () => {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);
    return null;
};

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        const lenis = window.__lenis;
        if (lenis) lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

const Shell = () => {
    const location = useLocation();
    // Detect the OAuth session_id synchronously during render (not in an effect)
    // so the callback is processed before any /auth/me check can race it.
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

const App = () => {
    return (
        <ErrorBoundary>
            <LanguageProvider>
                <BrowserRouter>
                    <SmoothScroll />
                    <ScrollToTop />
                    <Shell />
                    <Toaster
                        position="top-center"
                        theme="dark"
                        toastOptions={{
                            style: {
                                background: "#171A26",
                                border: "1px solid rgba(212,255,0,0.25)",
                                color: "#F4F5F7",
                            },
                        }}
                    />
                </BrowserRouter>
            </LanguageProvider>
        </ErrorBoundary>
    );
};

export default App;
